
import { defineComponent, onBeforeMount, reactive } from 'vue'

import NewButton from '@/ui-kit/NewButton/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

import filtersIcon from '@/assets/icons/filters.svg'

import { CompanyT } from '@/core/types/companies.types'
import { CityT } from '@/core/types/cities.types'
import { GetInvoicesParams } from '@/new-services/types/cms'
import { ManagerT } from '@/core/types/users.types'

import { cities } from '@/core/realisations/cities.realisation'
import { companies } from '@/core/realisations/companies.realisation'
import { users } from '@/core/realisations/users.realisation'

import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'
import sharedFilters from '@/common/composable/useSharedFiltersValues'

const SHIPMENT_TYPES = [
    { id: 1, name: 'Авто' },
    { id: 2, name: 'Авиа' },
]

const CASH_OPTIONS = [
    { id: 1, name: 'Да', value: '1' },
    { id: 2, name: 'Нет', value: '0' },
]

const DELIVERY_OPTIONS = [
    { id: 1, name: 'Да', value: '1' },
    { id: 2, name: 'Нет', value: '0' },
]

const PICKUP_OPTIONS = [
    { id: 1, name: 'Да', value: '1' },
    { id: 2, name: 'Нет', value: '0' },
]

const MANAGER_ROLE_ID = 20

export default defineComponent({
    components: {
        TheSelect,
        NewButton,
        TheInput,
        PageTitle,
        filtersIcon,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const { haveRole } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_open: false,
        })

        const filters = reactive({
            invoiceNumber: null as string | null,
            orderNumber: null as string | null,
            notCompanyIds: [] as number[],
            companyIds: [] as number[],
            shipmentTypeId: null as number | null,
            needCashSum: null as string | null,
            fromCityId: null as number | null,
            toCityId: null as number | null,
            managerIds: [] as number[],
            isSelfDelivery: null as string | null,
            isPickup: null as string | null,
            hasWaitListStatuses: 0,
            isDelaying: 0,
        })

        const data_state = reactive({
            companies: {
                is_loading: false,
                is_failed: false,
                list: [] as CompanyT[],
            },
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            managers: {
                is_loading: false,
                is_failed: false,
                list: [] as ManagerT[],
            },
        })

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({
                    page: 1,
                    getAll: 1,
                    isExtended: 0,
                    limit: 9999,
                })

                data_state.companies.list = response.data

                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка компаний ошибка',
                })
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(1, undefined, 9999)

                data_state.cities.list = response.data
                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка городов произошла ошибка',
                })
            } finally {
                data_state.cities.is_loading = false
            }
        }

        const onSubmit = () => {
            if (props.loading) return

            const params: GetInvoicesParams = {
                ...filters,
                needCashSum: filters.needCashSum ? Number(filters.needCashSum) : null,
                isSelfDelivery: null,
                isPickup: null,
            }

            if (filters.isSelfDelivery === '1') {
                params.isSelfDelivery = 1
            } else if (filters.isSelfDelivery === '0') {
                params.isSelfDelivery = 0
            }

            if (filters.isPickup === '1') {
                params.isPickup = 1
            } else if (filters.isPickup === '0') {
                params.isPickup = 0
            }

            emit('submit', params)
        }

        const onReset = () => {
            filters.invoiceNumber = null
            filters.orderNumber = null
            filters.notCompanyIds = []
            filters.companyIds = []
            filters.shipmentTypeId = null
            filters.needCashSum = null
            filters.fromCityId = null
            filters.toCityId = null
            filters.managerIds = []
            filters.isSelfDelivery = null
            filters.isPickup = null
            filters.hasWaitListStatuses = 0
            filters.isDelaying = 0

            emit('reset')
        }

        const toggle = () => (state.is_open = !state.is_open)

        const loadManagers = async (search?: string) => {
            try {
                data_state.managers.is_loading = true
                const response = await users.getManagers({
                    name: search,
                    statusId: 1,
                    isExtended: 0,
                })
                data_state.managers.list = response.data
                data_state.managers.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.managers.is_failed = true
            } finally {
                data_state.managers.is_loading = false
            }
        }

        const searchManagers = async (search_query: string) => {
            await loadManagers(search_query)
        }

        const unshiftFindedManager = async (manager: ManagerT) => {
            await loadManagers()
            data_state.managers.list.unshift(manager)
        }

        const selectAllManagers = () => {
            filters.managerIds = data_state.managers.list.map((manager) => manager.id)
        }

        const deselectAllManagers = () => {
            filters.managerIds = []
        }

        onBeforeMount(async () => {
            loadCompanies()
            loadCities()

            await loadManagers()

            if (haveRole(MANAGER_ROLE_ID) && sharedFilters.managerId) {
                filters.managerIds = [sharedFilters.managerId]
            } else {
                selectAllManagers()
            }

            onSubmit()
        })

        return {
            state,
            filters,
            data_state,
            selectAllManagers,
            deselectAllManagers,
            loadCompanies,
            loadCities,
            onSubmit,
            onReset,
            toggle,
            SHIPMENT_TYPES,
            CASH_OPTIONS,
            loadManagers,
            searchManagers,
            unshiftFindedManager,
            DELIVERY_OPTIONS,
            PICKUP_OPTIONS,
        }
    },
})
